'use client';

import { useState } from 'react';
import { AlertCircle, Info, Check, Edit, X, ExternalLink, ChevronDown } from '@/components/ui/icons';
import { cn } from '@/lib/utils';
import type { Recommendation } from '@audra-flow/types';

const typeConfig: Record<string, { bg: string; text: string; label: string }> = {
  'missing-persona': { bg: 'bg-[#f3e8ff]', text: 'text-[#8200db]', label: 'Missing Persona' },
  'data-gap': { bg: 'bg-[#e0e7ff]', text: 'text-[#432dd7]', label: 'Data Gap' },
  'conflicting-requirements': { bg: 'bg-[#ffe2e2]', text: 'text-[#c10007]', label: 'Conflicting Requirements' },
  'suggested-enhancement': { bg: 'bg-[#dbeafe]', text: 'text-[#1447e6]', label: 'Suggested Enhancement' },
  'structure-improvement': { bg: 'bg-[#cbfbf1]', text: 'text-[#00786f]', label: 'Structure Improvement' },
};

const defaultTypeStyle = { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Recommendation' };

const priorityConfig: Record<string, { bg: string; text: string; label: string }> = {
  high: { bg: 'bg-[#ffe2e2]', text: 'text-[#c10007]', label: 'High Priority' },
  medium: { bg: 'bg-[#fef9c2]', text: 'text-[#a65f00]', label: 'Medium Priority' },
  low: { bg: 'bg-[#dcfce7]', text: 'text-[#008236]', label: 'Low Priority' },
};

const defaultPriorityStyle = { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Priority' };

interface RecommendationCardProps {
  recommendation: Recommendation;
  onApply?: () => void;
  onEdit?: () => void;
  onDismiss?: () => void;
  onOpenRelated?: () => void;
}

export function RecommendationCard({
  recommendation,
  onApply,
  onEdit,
  onDismiss,
  onOpenRelated,
}: RecommendationCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const typeStyle = typeConfig[recommendation.type] || defaultTypeStyle;
  const priorityStyle = priorityConfig[recommendation.priority] || defaultPriorityStyle;

  // Get icon based on priority
  const PriorityIcon = recommendation.priority === 'high' ? AlertCircle : Info;

  // Clean description - remove JSON artifacts if present
  const cleanDescription = (desc: string) => {
    if (!desc) return 'No description available.';
    
    let cleaned = desc;
    
    // Remove markdown code blocks
    cleaned = cleaned.replace(/```json\s*/gi, '').replace(/```\s*/g, '');
    
    // Try to extract meaningful content from JSON-like strings
    if (cleaned.includes('"') && (cleaned.includes('{') || cleaned.includes('['))) {
      // Extract values from JSON-like patterns
      const patterns = [
        /"(?:recommendation|description|why_it_matters|rationale)":\s*"([^"]+)"/gi,
        /"([^"]{20,})"/g, // Any quoted string with 20+ chars
      ];
      
      const extracted: string[] = [];
      for (const pattern of patterns) {
        let match;
        while ((match = pattern.exec(cleaned)) !== null) {
          if (match[1] && match[1].length > 20) {
            extracted.push(match[1]);
          }
        }
      }
      
      if (extracted.length > 0) {
        cleaned = extracted.join(' ');
      } else {
        // Remove JSON syntax characters
        cleaned = cleaned
          .replace(/[{}\[\]]/g, '')
          .replace(/"[^"]+"\s*:/g, '')
          .replace(/"/g, '')
          .replace(/,\s*$/gm, '')
          .trim();
      }
    }
    
    return cleaned;
  };

  const fullDescription = cleanDescription(recommendation.description);
  const isLongDescription = fullDescription.length > 150;
  const shortDescription = isLongDescription ? fullDescription.slice(0, 150) + '...' : fullDescription;

  return (
    <div className="bg-white border border-gray-200 rounded-[14px] p-5 shadow-sm hover:shadow-md transition-shadow flex flex-col min-h-[236px]">
      {/* Header with tags */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <PriorityIcon className={cn('w-4 h-4 shrink-0', priorityStyle.text)} />
        <span className={cn('px-2.5 py-0.5 rounded-full text-xs font-medium', typeStyle.bg, typeStyle.text)}>
          {typeStyle.label}
        </span>
        <span className={cn('px-2.5 py-0.5 rounded-full text-xs font-medium', priorityStyle.bg, priorityStyle.text)}>
          {priorityStyle.label}
        </span>
      </div>

      {/* Title */}
      <h3 className="text-base font-medium text-gray-900 mb-2">
        {recommendation.title}
      </h3>

      {/* Description - line-clamp for consistent 2-line height when collapsed */}
      <div className="mb-3">
        <p className={cn(
          "text-sm text-gray-600 leading-relaxed",
          !isExpanded && "line-clamp-2"
        )}>
          {fullDescription}
        </p>
        {isLongDescription && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="mt-2 flex items-center gap-1 text-xs text-brand-primary hover:text-brand-primary/80 font-medium"
          >
            <ChevronDown className={cn('w-3.5 h-3.5 transition-transform', isExpanded && 'rotate-180')} />
            {isExpanded ? 'Show less' : 'Read more'}
          </button>
        )}
      </div>

      {/* Affects */}
      {recommendation.affects && recommendation.affects.length > 0 && (
        <div className="flex items-center gap-1.5 mb-4 flex-wrap">
          <span className="text-xs text-gray-400">Affects:</span>
          {recommendation.affects.slice(0, 3).map((item) => (
            <span key={item} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs truncate max-w-[150px]">
              {item}
            </span>
          ))}
          {recommendation.affects.length > 3 && (
            <span className="text-xs text-gray-400">+{recommendation.affects.length - 3}</span>
          )}
        </div>
      )}

      {/* Actions - wrap on small cards */}
      <div className="flex items-center gap-2 pt-3 border-t border-gray-100 flex-wrap mt-auto">
        <button
          onClick={onApply}
          className="h-8 px-3 flex items-center gap-1.5 bg-brand-primary text-white rounded-lg hover:bg-brand-primary/90 transition-colors text-xs font-medium"
        >
          <Check className="w-3.5 h-3.5" />
          Apply
        </button>

        <button
          onClick={onEdit}
          className="h-8 px-3 flex items-center gap-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-xs text-gray-700"
        >
          <Edit className="w-3.5 h-3.5" />
          Edit
        </button>

        <button
          onClick={onDismiss}
          className="h-8 px-3 flex items-center gap-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-xs text-gray-700"
        >
          <X className="w-3.5 h-3.5" />
          Dismiss
        </button>

        <button
          onClick={onOpenRelated}
          className="h-8 w-8 flex items-center justify-center border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-gray-700 ml-auto"
          title="View Related"
        >
          <ExternalLink className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

